import api from '@/config/axios';
import type { IntegrationTestResult } from '@/types/integrationTest';
import type { PurchaseOrderDisplayStatus } from '@/views/PedidosCompra/purchaseOrderStatus';

/** O que o Protheus responde. O status EXIBIDO é PurchaseOrderDisplayStatus. */
export type PurchaseOrderStatus = 'pendente' | 'aprovado' | 'recusado' | 'liberado';

export interface PurchaseOrderItem {
  id: number;
  purchase_order_id: number;
  item: string;
  produto: string | null;
  fornecedor: string | null;
  descricao: string | null;
  obs_ped: string | null;
  valor: number | string;
  total: number | string;
  qtde: number | string;
  prc_pedido: number | string;
  prc_rateio: number | string;
  rateio: boolean;
  percrat: number | string;
  ccontabil: string | null;
  dccontabil: string | null;
  itconta: string | null;
  ditconta: string | null;
  ccusto: string | null;
  dccusto: string | null;
}

export interface PurchaseOrderNivel {
  nivel: string;
  nome: string;
  email: string | null;
  status: PurchaseOrderStatus;
  obs: string | null;
}

/** OPERACAO do Protheus: 4 = aprovado, 6 = reprovado, null = pendente. */
export type PurchaseOrderOperacao = 4 | 6;

export interface PurchaseOrderDocumento {
  id: number;
  nivel: string | null;
  documento: string | null;
  aprovador_nome: string | null;
  aprovador_email?: string | null;
  // Rateio por centro de custo — presentes quando o pedido é rateado (ver Task 6).
  ccusto?: string | null;
  dccusto?: string | null;
  valor?: number | string | null;
  percrat?: number | string | null;
  operacao: PurchaseOrderOperacao | null;
  obs: string | null;
  decided_at: string | null;
}

export interface PurchaseOrderApprover {
  id: number;
  email: string;
  display_name: string | null;
  nome: string | null;
  sAMAccountName: string | null;
}

export type PurchaseOrderTipo = 'pedido' | 'contrato';

/** Rótulo de exibição do tipo do documento (campo TIPO do Protheus). */
export function purchaseOrderTipoLabel(tipo?: string | null): string {
  return tipo === 'contrato' ? 'Contrato de Compra' : 'Pedido de Compra';
}

export interface PurchaseOrder {
  id: number;
  pedido: string;
  tipo?: PurchaseOrderTipo;
  empresa: string;
  filial: string;
  solicitante_nome: string;
  solicitante_email: string | null;
  solicitante_usuario: string | null;
  data: string;
  aprovador_nome: string | null;
  aprovador_user_id: number | null;
  ccusto: string | null;
  dccusto: string | null;
  valor_total: number | string;
  status: PurchaseOrderStatus;
  /** Status por-usuário: inclui 'encerrado', que é derivado e não existe no ERP. */
  meu_status?: PurchaseOrderDisplayStatus;
  nivel: string | null;
  obs: string | null;
  obs_pedido: string | null;
  niveis?: PurchaseOrderNivel[] | null;
  documentos?: PurchaseOrderDocumento[];
  can_decide?: boolean;
  // Pedido pendente ausente do payload do Protheus: o sync não o atualiza mais e a
  // decisão pelo Hub fica bloqueada (verificação manual no ERP).
  fora_do_fluxo?: boolean;
  /**
   * ISO de quando o Protheus deixou de pedir aprovação — preenchido só depois da
   * carência (1h), quando `meu_status` vira 'encerrado'. Antes disso o pedido
   * segue pendente e o chip é "Confirmando no Protheus…".
   */
  encerrado_desde?: string | null;
  // Id LOCAL do PurchaseOrderDocument acionável (rateio por-CC). Legado: o id
  // churna a cada sync — use `meu_documento` na decisão.
  meu_documento_id?: number | null;
  // Nº do documento no Protheus (chave ESTÁVEL entre recriações do sync) — é o
  // que o drawer manda em `documento` na decisão, sem risco de cair em outro CC.
  meu_documento?: string | null;
  // Aprovador/CC do PRÓPRIO usuário logado (rateio por-CC) — mesma resolução por
  // e-mail usada no detalhe; permite a lista mostrar o aprovador correto por-viewer.
  meu_aprovador_nome?: string | null;
  meu_dccusto?: string | null;
  /**
   * Recorte por centro de custo: quando o usuário é aprovador de um CC (e não é
   * admin da rota), `valor_total`, `items` e `documentos` trazem SÓ o CC dele —
   * o rateio é paralelo, cada cadeia decide a sua parte. Lista devolve boolean;
   * o detalhe devolve os códigos. `null`/`false` = pedido completo.
   */
  recorte_cc?: string[] | boolean | null;
  /** Valor cheio do pedido no Protheus, antes do recorte e das contas excluídas. */
  valor_bruto_protheus?: number;
  /**
   * CC do card de Orçamento: o do PRÓPRIO usuário quando ele é aprovador do
   * pedido (`meu: true`) — inclusive para o admin master, que vê o pedido
   * completo mas responde por um CC. Sem CC dele, é o do cabeçalho do Protheus.
   */
  cc_orcamento?: { codigo: string | null; nome: string | null; meu: boolean };
  /** Peso deste pedido no CC de `cc_orcamento` — não o total do rateio. */
  valor_cc_orcamento?: number;
  /** Quantos itens sumiram do detalhe (outros CCs + contas fora do gasto de CC). */
  itens_ocultos?: number;
  email_sent_at: string | null;
  protheus_hash: string;
  created_at: string;
  updated_at: string;
  items?: PurchaseOrderItem[];
  items_count?: number;
  cliente_projeto_labels?: string[];
  approver?: PurchaseOrderApprover | null;
}

export interface PurchaseOrderCcConsumo {
  ano: number;
  ccusto: string;
  /** Pedidos LIBERADOS do CC no ano (consumo efetivado). */
  pedidos: number;
  /** Pedidos pendentes do CC no ano — compromisso, não consumo. */
  pedidos_pendentes: number;
  qtd_pedidos: number;
  reembolsos: number;
  reembolsos_qtd: number;
  /**
   * REPASSE do CC no ano. NÃO entra no consumido (não consome orçamento), mas
   * aparece em linha própria — concentra-se em Fidelização e no comercial.
   */
  repasses: number;
  repasses_qtd: number;
  /** pedidos + reembolsos (sem repasse). */
  consumido: number;
  /** Orçamento previsto do CC — null enquanto o PCO não tiver linha por CC. */
  previsto: number | null;
}

export interface PaginatedPurchaseOrders {
  items: PurchaseOrder[];
  total: number;
  page: number;
  limit: number;
}

export interface PurchaseOrderStats {
  total: number;
  pendente: number;
  aprovado: number;
  recusado: number;
  liberado: number;
  /** Pedidos que o Protheus não pede mais para aprovar (estado derivado). */
  encerrado: number;
  valor_total: number;
}

export interface PurchaseOrderFilters {
  status?: PurchaseOrderDisplayStatus;
  filial?: string;
  ccusto?: string;
  aprovador?: string;
  dataIni?: string;
  dataFim?: string;
  valorMin?: number;
  valorMax?: number;
  search?: string;
  page?: number;
  limit?: number;
  paginate?: boolean;
}

export interface PurchaseOrderFiltersOptions {
  filials: string[];
  costCenters: Array<{ codigo: string; descricao: string }>;
  approvers: string[];
}

export interface PurchaseOrderSettings {
  sync_enabled: string;
  sync_cron_expression: string;
  auto_email_enabled: string;
  email_fallback_address: string;
  protheus_api_url: string;
  protheus_api_user: string;
  protheus_api_pass_set: boolean;
  // 2ª conexão (Siminova)
  protheus_b_enabled: string;
  protheus_b_url: string;
  protheus_b_user: string;
  protheus_b_pass_set: boolean;
}

export interface PurchaseOrderSettingsResponse {
  settings: PurchaseOrderSettings;
  lastSummary: {
    inserted: unknown[];
    updated: unknown[];
    unchanged: number;
    failed: number;
    totalRowsFromProtheus: number;
    totalOrdersGrouped: number;
    ranAt: string;
  } | null;
  cronRunning: boolean;
  emailServiceEnabled: boolean;
}

export interface PurchaseOrderSyncResponse {
  ok: boolean;
  inserted?: number;
  updated?: number;
  unchanged?: number;
  failed?: number;
  totalRowsFromProtheus?: number;
  totalOrdersGrouped?: number;
  reason?: string;
}

function toQuery(filters: PurchaseOrderFilters): string {
  const params = new URLSearchParams();
  if (filters.status) params.append('status', filters.status);
  if (filters.filial) params.append('filial', filters.filial);
  if (filters.ccusto) params.append('ccusto', filters.ccusto);
  if (filters.aprovador) params.append('aprovador', filters.aprovador);
  if (filters.dataIni) params.append('dataIni', filters.dataIni);
  if (filters.dataFim) params.append('dataFim', filters.dataFim);
  if (filters.valorMin !== undefined) params.append('valorMin', String(filters.valorMin));
  if (filters.valorMax !== undefined) params.append('valorMax', String(filters.valorMax));
  if (filters.search) params.append('search', filters.search);
  if (filters.page !== undefined) params.append('page', String(filters.page));
  if (filters.limit !== undefined) params.append('limit', String(filters.limit));
  if (filters.paginate === false) params.append('paginate', 'false');
  const q = params.toString();
  return q ? `?${q}` : '';
}

export const purchaseOrdersApi = {
  async list(filters: PurchaseOrderFilters = {}): Promise<PaginatedPurchaseOrders> {
    const { data } = await api.get(`/purchase-orders${toQuery(filters)}`);
    return data;
  },

  async stats(filters: PurchaseOrderFilters = {}): Promise<PurchaseOrderStats> {
    const { data } = await api.get(`/purchase-orders/stats${toQuery(filters)}`);
    return data;
  },

  async getById(id: number): Promise<PurchaseOrder> {
    const { data } = await api.get(`/purchase-orders/${id}`);
    return data;
  },

  /**
   * Consumo do CC no ano — card "Orçamento do CC". `previsto: null` porque o
   * orçamento (PCO) ainda não existe por centro de custo: o card mostra o
   * consumido e diz o que falta.
   */
  async ccConsumo(ccusto: string, ano?: number): Promise<PurchaseOrderCcConsumo> {
    const q = ano ? `?ano=${ano}` : '';
    const { data } = await api.get(`/purchase-orders/cc-consumo/${encodeURIComponent(ccusto)}${q}`);
    return data;
  },

  // Pedidos aguardando a decisão do usuário logado (sino do menu).
  async newCount(): Promise<{ count: number }> {
    const { data } = await api.get('/purchase-orders/new-count');
    return data;
  },

  async filtersOptions(): Promise<PurchaseOrderFiltersOptions> {
    const { data } = await api.get('/purchase-orders/filters-options');
    return data;
  },

  async decide(
    id: number,
    payload: { operacao: PurchaseOrderOperacao; justificativa?: string; documento?: string; documentId?: number }
  ): Promise<PurchaseOrder> {
    // 60s SÓ nesta chamada (o padrão do app é 30s): o write-back espera o ERP
    // por até 45s. Com 30s aqui, o front desistia ANTES do backend em toda
    // decisão lenta — a aprovação aplicava e a resposta se perdia, e o
    // aprovador via erro num pedido que tinha acabado de aprovar.
    const { data } = await api.post(`/purchase-orders/${id}/decision`, payload, { timeout: 60000 });
    return data;
  },

  async sendEmail(id: number): Promise<{ ok: boolean; email_sent_at: string | null }> {
    const { data } = await api.post(`/purchase-orders/${id}/send-email`);
    return data;
  },

  async sendEmailBatch(ids: number[]): Promise<{ ok: boolean; sent: number; failed: number; total: number }> {
    const { data } = await api.post('/purchase-orders/send-emails', { ids });
    return data;
  },

  async syncNow(): Promise<PurchaseOrderSyncResponse> {
    const { data } = await api.post('/purchase-orders/sync-now');
    return data;
  },

  async getSettings(): Promise<PurchaseOrderSettingsResponse> {
    const { data } = await api.get('/purchase-orders/settings');
    return data;
  },

  async testConnection(override?: {
    source?: 'principal' | 'siminova';
    protheus_api_url?: string;
    protheus_api_user?: string;
    protheus_api_pass?: string;
    protheus_b_url?: string;
    protheus_b_user?: string;
    protheus_b_pass?: string;
  }): Promise<IntegrationTestResult> {
    const { data } = await api.post('/purchase-orders/test-connection', override ?? {});
    return data;
  },

  async updateSettings(patch: Partial<{
    sync_enabled: boolean | string;
    sync_cron_expression: string;
    auto_email_enabled: boolean | string;
    email_fallback_address: string;
    protheus_api_url: string;
    protheus_api_user: string;
    protheus_api_pass: string;
    protheus_b_enabled: boolean | string;
    protheus_b_url: string;
    protheus_b_user: string;
    protheus_b_pass: string;
  }>): Promise<{ ok: boolean; settings: PurchaseOrderSettings; cronRunning: boolean }> {
    const { data } = await api.put('/purchase-orders/settings', patch);
    return data;
  },
};
